import React, { useEffect, useMemo, useState } from "react";
import * as api from "./csaApi";

/**
 * The three settings that change without a new framework version (§10d).
 *
 * Questions need a new version because every audit is pinned to one. A weight is
 * not a question, and a submitted audit carries a frozen copy of all three
 * tables (§3) — so editing these can never move a report that has already gone
 * out to a client.
 *
 * What it *does* move is every audit still in draft, silently and immediately.
 * That is the whole risk of this screen, so it is stated before saving and the
 * count comes back after.
 *
 * Each section saves on its own. They are independent, and one Save for
 * everything would make a small weight correction feel like a bigger act than
 * it is — while also making a mistake in one table block a fix in another.
 */
export default function CsaScoringSettings({ fw, onSaved }) {
  const [weights, setWeights] = useState([]);
  const [bands, setBands] = useState([]);
  const [scoring, setScoring] = useState([]);
  const [busy, setBusy] = useState(null);
  const [errors, setErrors] = useState(null);
  const [flash, setFlash] = useState(null);

  /* Reset the drafts whenever the server's copy changes. */
  useEffect(() => {
    if (!fw) return;
    setWeights(fw.weights.map((w) => ({ ...w })));
    setBands(fw.bands.map((b) => ({ ...b })));
    setScoring(fw.scoring.map((s) => ({ ...s })));
  }, [fw]);

  const drafts = useMemo(() => {
    const row = (fw && fw.usage ? fw.usage : []).find(
      (u) => u.status === "draft" && u.framework_version === (fw.active && fw.active.version)
    );
    return row ? row.count : 0;
  }, [fw]);

  const weightTotal = useMemo(
    () => Math.round(weights.reduce((n, w) => n + (Number(w.weight) || 0), 0) * 100) / 100,
    [weights]
  );
  const weightsOk = Math.abs(weightTotal - 100) <= 0.01;

  const dirty = (a, b) => JSON.stringify(a) !== JSON.stringify(b);
  const weightsDirty = fw && dirty(weights, fw.weights);
  const bandsDirty = fw && dirty(bands, fw.bands);
  const scoringDirty = fw && dirty(scoring, fw.scoring);

  const save = async (what, payload) => {
    setBusy(what);
    setErrors(null);
    setFlash(null);
    try {
      const r = await api.adminSaveConfig(payload);
      setFlash(r.note);
      if (onSaved) onSaved();
    } catch (e) {
      if (e.body && Array.isArray(e.body.errors)) setErrors(e.body.errors);
      else setErrors([e.message]);
    } finally {
      setBusy(null);
    }
  };

  if (!fw) return null;

  return (
    <section className="ca-card">
      <div className="ca-card-head">
        <h4>Scoring settings</h4>
        <span className="ca-badge">no new version needed</span>
      </div>

      <p className="ca-muted">
        These three change without publishing a version. Submitted audits and
        issued reports carry their own frozen copy and cannot move.
        {drafts ? (
          <>
            {" "}
            <strong>
              {drafts} audit{drafts === 1 ? "" : "s"} still in draft will be
              rescored.
            </strong>
          </>
        ) : (
          " No audit is in draft, so nothing is rescored."
        )}
      </p>

      {errors ? (
        <div className="fw-errors">
          <strong>Nothing was saved.</strong>
          <ul>
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {flash ? <p className="ca-flash">{flash}</p> : null}

      <div className="ca-cols">
        {/* ---------------- weights ---------------- */}
        <div>
          <h5>
            Pillar weights{" "}
            <span className={weightsOk ? "ca-ok" : "ca-bad"}>
              total {weightTotal}
              {weightsOk ? "" : " — must be 100"}
            </span>
          </h5>

          <table className="ca-table">
            <tbody>
              {weights.map((w, i) => (
                <tr key={w.pillar_no}>
                  <td>
                    P{w.pillar_no} {w.pillar_name}
                  </td>
                  <td className="n">
                    <input
                      className="sc-num"
                      type="number"
                      step="0.01"
                      min="0"
                      value={w.weight}
                      onChange={(e) => {
                        const v = e.target.value;
                        setWeights((prev) =>
                          prev.map((x, j) => (j === i ? { ...x, weight: v === "" ? "" : Number(v) } : x))
                        );
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* The argument this screen exists to allow, stated once. */}
          <p className="ca-muted sc-hint">
            P4 holds 61 of the 163 questions but one sixth of the score. Whether
            that is right is a judgement, not a bug — this is where it is made.
          </p>

          <div className="sc-actions">
            <button
              type="button"
              className="ca-save"
              disabled={!weightsDirty || !weightsOk || busy === "weights"}
              title={weightsOk ? "" : "The weights must total 100"}
              onClick={() =>
                save("weights", {
                  weights: weights.map((w) => ({ pillar_no: w.pillar_no, weight: Number(w.weight) })),
                })
              }
            >
              {busy === "weights" ? "Saving…" : "Save weights"}
            </button>
            {weightsDirty ? (
              <button type="button" className="fw-plain"
                      onClick={() => setWeights(fw.weights.map((w) => ({ ...w })))}>
                Reset
              </button>
            ) : null}
          </div>
        </div>

        {/* ---------------- bands ---------------- */}
        <div>
          <h5>Maturity bands</h5>
          <table className="ca-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="n">From</th>
                <th className="n">To</th>
              </tr>
            </thead>
            <tbody>
              {bands.map((b, i) => (
                <tr key={i}>
                  <td>
                    <span className="ca-swatch" style={{ background: b.color || "#ccc" }} />
                    <input
                      className="sc-text"
                      type="text"
                      value={b.name}
                      onChange={(e) =>
                        setBands((prev) => prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
                      }
                    />
                  </td>
                  {["min_pct", "max_pct"].map((k) => (
                    <td className="n" key={k}>
                      <input
                        className="sc-num"
                        type="number"
                        step="0.01"
                        value={b[k]}
                        onChange={(e) => {
                          const v = e.target.value;
                          setBands((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, [k]: v === "" ? "" : Number(v) } : x))
                          );
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          <p className="ca-muted sc-hint">
            They must cover 0 to 100 with no gap and no overlap. The engine takes
            the highest band a score reaches, so a gap quietly borrows the band
            below rather than failing.
          </p>

          <div className="sc-actions">
            <button
              type="button"
              className="ca-save"
              disabled={!bandsDirty || busy === "bands"}
              onClick={() => save("bands", { bands })}
            >
              {busy === "bands" ? "Saving…" : "Save bands"}
            </button>
            {bandsDirty ? (
              <button type="button" className="fw-plain"
                      onClick={() => setBands(fw.bands.map((b) => ({ ...b })))}>
                Reset
              </button>
            ) : null}
          </div>
        </div>

        {/* ---------------- answer values ---------------- */}
        <div>
          <h5>Answer values</h5>
          <table className="ca-table">
            <thead>
              <tr>
                <th>Answer</th>
                <th className="n">Worth</th>
                <th className="c" title="Whether this answer is part of the denominator">
                  Counted
                </th>
              </tr>
            </thead>
            <tbody>
              {scoring.map((s, i) => (
                <tr key={s.answer_state}>
                  <td>{s.answer_state}</td>
                  <td className="n">
                    <input
                      className="sc-num"
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={s.value}
                      onChange={(e) => {
                        const v = e.target.value;
                        setScoring((prev) =>
                          prev.map((x, j) => (j === i ? { ...x, value: v === "" ? "" : Number(v) } : x))
                        );
                      }}
                    />
                  </td>
                  <td className="c">
                    <input
                      type="checkbox"
                      checked={!!s.counts_in_denominator}
                      aria-label={`${s.answer_state} counts in the denominator`}
                      onChange={() =>
                        setScoring((prev) =>
                          prev.map((x, j) =>
                            j === i ? { ...x, counts_in_denominator: !x.counts_in_denominator } : x
                          )
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="ca-muted sc-hint">
            “Counted” is the half that matters: N/A scores nothing <em>and</em>
            leaves the denominator, which is what stops a scoped-out question
            dragging a site's percentage down.
          </p>

          <div className="sc-actions">
            <button
              type="button"
              className="ca-save"
              disabled={!scoringDirty || busy === "scoring"}
              onClick={() => save("scoring", { scoring })}
            >
              {busy === "scoring" ? "Saving…" : "Save values"}
            </button>
            {scoringDirty ? (
              <button type="button" className="fw-plain"
                      onClick={() => setScoring(fw.scoring.map((s) => ({ ...s })))}>
                Reset
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
